<template>
  <article class="editable-product-preview">
    <article id="reversible-options" v-if="isActiveProductReversible">
      <button
        :disabled="activeDecoration"
        class="btn btn-reverse"
        @click="$emit('changeReversibleView')"
      >
        <img
          src="~@/assets/icons/ui/reverse.png"
          alt="reverse icon"
          :class="{
            inside: reversibleView === 'Inside',
            outside: reversibleView === 'Outside',
          }"
        />
        {{ reversibleView }}
      </button>
      <p class="slate-light">Click to customize other side</p>
    </article>

    <article id="threekit-player-container">
      <!-- 3D -->
      <div id="threekit-player"></div>
      <!-- Camera Controls -->
      <article id="camera-controls-container">
        <button
          @click="changeView('previous')"
          class="arrow-buttons"
          title="rotate to left"
        >
          <img src="@/assets/3d-left-arrow.png" alt="rotate to left arrow" />
        </button>
        <div class="zoom-buttons">
          <button class="btn text-grey-dark" @click="zoom(-2)">
            <img src="@/assets/icons/ui/zoom-out.png" alt="zoom out" />
          </button>
          <span>Zoom</span>
          <button class="btn text-grey-dark" @click="zoom(2)">
            <img src="@/assets/icons/ui/zoom-in.png" alt="zoom in" />
          </button>
        </div>
        <button
          @click="changeView('next')"
          class="arrow-buttons"
          title="rotate to right"
        >
          <img src="@/assets/3d-right-arrow.png" alt="rotate to right arrow" />
        </button>
      </article>
      <!-- Loader -->
      <transition name="fade" v-if="playerLoader">
        <section id="player-loader">
          <div class="loader-message">
            <p class="forza-bold">{{ playerLoaderMessage }}</p>
          </div>
        </section>
      </transition>
    </article>

    <article id="product-information">
      <div class="product-title">
        <h3 class="slate-bold">Product Details</h3>

        <div class="stock-price-guarantee" v-if="!isSublimated3D">
          <div class="guarantee-title">
            <img
              src="@/assets/icons/ui/pricetag-outline.svg"
              alt="ticket image"
            />
            <span>request a quote for the best price</span>
          </div>
          <p>
            Our team works directly with manufacturers to give you the best
            price possible
          </p>
        </div>
      </div>

      <div class="product-prices">
        <div class="product-price-row" v-if="showpRices">
          <span class="slate-bold">{{ productPrice }} </span>
          <span>Per piece</span>

          <!-- Dealer Cost -->
          <span class="dealer-cost" v-if="dealerMode"
            >(Your Price: ${{
              activeProduct &&
              activeProduct.dealerPrice &&
              activeProduct.dealerPrice.toFixed(2)
            }})</span
          >
          <div class="tooltip-container" v-if="dealerMode">
            <img
              src="@/assets/icons/ui/information-circle-outline.svg"
              alt="info icon"
            />
            <div class="tooltip-info">Only you can see this</div>
          </div>
        </div>
        <div
          class="product-price-row"
          v-if="!isSublimated3D && decorationsPrice && showpRices"
        >
          <span class="slate-bold">${{ decorationsPrice.toFixed(2) }} </span>
          <span>Decoration charge per piece</span>

          <!-- Dealer Cost -->
          <span class="dealer-cost" v-if="dealerMode"
            >(Your Price: ${{ (decorationsPrice / 2).toFixed(2) }})</span
          >
          <div class="tooltip-container" v-if="dealerMode">
            <img
              src="@/assets/icons/ui/information-circle-outline.svg"
              alt="info icon"
            />
            <div class="tooltip-info">Only you can see this</div>
          </div>
        </div>
        <!-- <div class="product-price-row">
          <span class="slate-bold">{{ productLeadTime }} Business Days </span>
          <span>Lead Time</span>
        </div> -->
      </div>

      <ul class="product-info-list">
        <li v-for="(item, i) in description" :key="i + '-description'">
          {{ item }}
        </li>
      </ul>
    </article>
  </article>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
export default {
  name: "ActiveProductPreview",
  props: ["product"],

  computed: {
    ...mapState("project", ["activeProduct"]),
    ...mapState("snapsheet", ["showPrices"]),
    ...mapState(["dealer"]),
    ...mapState("auth", ["dealerMode"]),
    ...mapState("threekit", [
      "activeDecoration",
      "decorationsPrice",
      "playerLoader",
      "playerLoaderMessage",
      "reversibleView",
    ]),
    ...mapGetters("threekit", ["isActiveProductReversible", "isSublimated3D"]),

    productPrice() {
      const price =
        this.activeProduct &&
        this.activeProduct.productPrice &&
        this.activeProduct.productPrice.toFixed(2);

      return isNaN(price) || !price ? "Calculating price..." : `$${price}`;
    },

    productLeadTime() {
      return this.activeProduct?.production_time;
    },

    description() {
      return this.activeProduct?.description;
    },

    showpRices() {
      return this.showPrices;
    },
  },

  methods: {
    ...mapActions("threekit", [
      "setLoadingState",
      "change3DView",
      "changeReversibleView",
    ]),

    async changeView(step) {
      const VIEWS_3D = ["Front", "Right", "Back", "Left"];

      //Get the Stage configurator
      const stageConfigurator = await window.stageApi.getConfigurator();
      const stageActualView = stageConfigurator
        ?.getDisplayAttributes()
        ?.filter((attr) => attr?.name === "View")?.[0]?.value;

      //Exception: The 'Default' view. Replace it as 'Front'
      const actualView =
        stageActualView === "Default" ? "Front" : stageActualView;
      //Find the index of the actual view
      const actualViewIndex = Number(VIEWS_3D.indexOf(actualView));

      //Change the view based on the step behaviour
      let newViewIndex;
      if (step === "previous") {
        newViewIndex =
          actualViewIndex === 0 ? VIEWS_3D.length - 1 : actualViewIndex - 1;
      } else {
        newViewIndex =
          actualViewIndex === VIEWS_3D.length - 1 ? 0 : actualViewIndex + 1;
      }
      const newView = VIEWS_3D[newViewIndex];
      //Finally, change the view
      await this.change3DView({ view: newView });
    },

    zoom(zoomToApply) {
      const player = window.playerApi;
      player.cameraController.zoom(zoomToApply);
    },
  },
};
</script>

<style lang="scss" scoped>
.editable-product-preview {
  background: white;
  width: 50%;
  display: flex;
  flex-direction: column;
  height: auto;
  position: relative;

  #threekit-player-container {
    width: 100%;
    height: 70vh;
    position: relative;
    max-height: 40em;
    #threekit-player {
      height: 100%;
      width: 100%;
    }

    ::v-deep svg {
      width: 100%;
    }

    #camera-controls-container {
      display: flex;
      justify-content: space-between;
      margin: 0 auto;
      position: absolute;
      bottom: 0;
      z-index: 1;
      /* background: white; */
      width: 94%;
      bottom: 0%;
      left: 3%;
      .btn {
        img {
          margin-right: 0.5rem;
        }
      }

      button.arrow-buttons {
        background: none;
        img {
          max-height: 50px;
          width: 100%;
        }

        &:hover {
          img {
            filter: drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.233));
          }
        }
      }

      .zoom-buttons {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }

    #player-loader {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.233);
      display: flex;
      align-items: center;
      justify-content: center;

      .loader-message {
        border-radius: 6px;
        padding: 0.75rem;
        background: $grey-darker;
        p {
          color: white;
          font-size: 1.5rem;
          text-transform: capitalize;
        }
      }
    }
  }

  #product-information {
    display: flex;
    flex-direction: column;
    height: 30%;
    width: 100%;
    overflow-x: visible;
    & > * {
      padding: 0.5rem 2rem;
    }

    .product-title {
      display: flex;
      justify-content: space-between;
      position: relative;
      .stock-price-guarantee {
        display: flex;
        flex-direction: column;
        align-items: start;
        position: absolute;
        top: 5%;
        left: 60%;
        width: 60%;
        padding: 1rem;
        padding-top: 0.5rem;
        transition: all 0.2s;
        p {
          opacity: 0;
          transition: all 0.2s;
        }
        &:hover {
          box-shadow: 0px 9px 18px rgb(40 42 48 / 20%);
          border-radius: 6px;
          background: white;
          border-radius: 6px;
          z-index: 100;
          p {
            opacity: 1;
          }
        }
        img {
          margin-right: 0.4rem;
          width: 1.2rem;
          height: 1.2rem;
        }
        .guarantee-title {
          margin-bottom: 1.25rem;
          span {
            color: $showroomColor;
            
            text-transform: capitalize; 
          }
        }

        @media screen and (max-width: 1366px) {
          left: 60%;
        }
      }
    }
    .product-prices {
      background: $grey-lightest;

      * {
        color: $grey-dark;
        font-size: 1.25rem;
      }
      .product-price-row {
        display: flex;

        span.slate-bold {
          margin-right: 0.25rem;
        }
      }
      .dealer-cost {
        margin-left: 1rem;
        margin-right: 1rem;
        color: $placeholder;
      }

      .tooltip-container {
        position: relative;
        width: 1rem;
        .tooltip-info {
          background: $dark;
          position: absolute;
          color: $white;
          box-shadow: 0px 0px 17px -8px #00000078;
          width: max-content;
          left: -65%;
          bottom: -2.3rem;
          padding: 0.5rem;
          border-radius: 6px;
          z-index: 10;
          opacity: 0;
          transition: opacity 0.1s ease-in;
        }

        &:hover {
          .tooltip-info {
            opacity: 1;
          }
        }
      }
    }

    .product-info-list {
      // overflow-y: auto;
      li {
        color: $grey-dark;
        margin-left: 2rem;
      }
    }
  }

  #reversible-options {
    margin: 1rem;
    position: absolute;
    top: 1rem;
    left: 1rem;
    z-index: 1;
    .btn-reverse {
      background-color: $grey-lightest;
      border: 1px solid $borderColor;
      color: $grey-dark;
      padding-left: 0;

      img {
        margin-right: 0.5rem;
        transition: all 0.5s;
        &.inside {
          animation: inside-rotation 0.5s forwards;
        }
        &.outside {
          animation: outside-rotation 0.5s forwards;
        }
      }
    }

    p {
      font-size: 0.85rem;
      color: $grey-light;
    }

    @keyframes inside-rotation {
      100% {
        transform: rotateY(180deg);
      }
    }

    @keyframes outside-rotation {
      0% {
        transform: rotateY(180deg);
      }
    }
  }
}
</style>
